import React from 'react';
import { addDays, parseISO, subDays } from 'date-fns';
import update from 'immutability-helper';
import { v4 as uuidV4 } from 'uuid';
import { Client, EditMember, Member, MemberGroup, Plan } from '../models';
import { CoverageTier, Relationship } from '../models/enums';
import { SsnDatabase } from '../ssn-db';
import { formatISODate, today } from '../util';

export type Action =
  | { type: 'revert' }
  | { type: 'terminateMemberGroup' }
  | { type: 'cancelTerminateMemberGroup' }
  | { type: 'updateCoverageTier'; coverageTier: CoverageTier }
  | { type: 'updatePlanTimeFrame'; timeFrame: string; plans: Plan[] | undefined }
  | { type: 'updatePlan'; plan: Plan }
  | { type: 'updatePrimary'; field: string; value: string | null }
  | { type: 'updatePrimaryBenefitStartDate'; value: string | null }
  | { type: 'updatePrimaryBenefitEndDate'; value: string | null }
  | { type: 'updateDependent'; index: number; field: string; value: string | null }
  | { type: 'updateDependentBenefitStartDate'; index: number; value: string | null }
  | { type: 'updateDependentBenefitEndDate'; index: number; value: string | null }
  | { type: 'updateDependentAddressIsSameAsPrimary'; index: number; value: boolean }
  | { type: 'addChild' }
  | { type: 'addSpouse' }
  | { type: 'deleteDependent'; index: number }
  | { type: 'terminateDependent'; index: number }
  | { type: 'cancelTerminateDependent'; index: number }
  | { type: 'updateInvoiceGroup'; invoiceGroup: string };

export interface State {
  initState?: State;
  canRevert?: boolean;
  isDirty?: boolean;
  ssnDB: SsnDatabase;
  isNew: boolean;
  id: string;
  clientId: number;
  benefitPlanId?: number;
  benefitPlanName?: string;
  benefitPlanTimeFrame?: string;
  plan?: Plan;
  organizationId: string;
  coverageTier?: CoverageTier;
  primary: EditMember;
  dependents: EditMember[];
  canAddChild: boolean;
  canAddSpouse: boolean;
  focusDependentIndex?: number;
  invoiceGroup?: string;
}

function canAddChild({ coverageTier }: { coverageTier?: CoverageTier }) {
  return coverageTier === CoverageTier.EMPLOYEE_CHILDREN || coverageTier === CoverageTier.FAMILY;
}

function canAddSpouse({
  coverageTier,
  dependents,
}: {
  coverageTier?: CoverageTier;
  dependents: Pick<Member, 'relationshipCode' | 'benefitEndDate'>[];
}) {
  return (
    (coverageTier === CoverageTier.EMPLOYEE_SPOUSE || coverageTier === CoverageTier.FAMILY) &&
    dependents.every(d => d.relationshipCode !== Relationship.SPOUSE || d.benefitEndDate)
  );
}

function* allMembers(state: State) {
  yield state.primary;
  for (const element of state.dependents) yield element;
}

function editReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'revert': {
      if (!state.initState) return state;

      const next = update(state, {
        $merge: state.initState,
        isDirty: { $set: false },
        canRevert: { $set: false },
      });

      for (const { ssn, relationshipCode } of allMembers(state)) next.ssnDB.deregister(ssn, relationshipCode);
      for (const { ssn, relationshipCode } of allMembers(next)) next.ssnDB.register(ssn, relationshipCode);

      return next;
    }

    case 'cancelTerminateMemberGroup': {
      const benefitEndDate = { $set: undefined };
      const isCancelTerminated = { $set: true };
      return update(state, {
        primary: { benefitEndDate, isCancelTerminated },
        dependents: d => d.map(v => update(v, { benefitEndDate, isCancelTerminated })),
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });
    }

    case 'terminateMemberGroup': {
      const benefitEndDate = { $set: today() };
      return update(state, {
        primary: { benefitEndDate },
        dependents: d => d.map(v => (v.isCancelTerminated ? v : update(v, { benefitEndDate }))),
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });
    }

    case 'updatePlanTimeFrame': {
      // sets plan fields if there is only one plan available, otherwise clears plan fields
      const plan = action.plans?.length === 1 ? action.plans[0] : undefined;
      return update(state, {
        plan: { $set: plan },
        benefitPlanId: { $set: plan?.id },
        benefitPlanName: { $set: plan?.name },
        benefitPlanTimeFrame: { $set: action.timeFrame },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });
    }

    case 'updatePlan':
      return update(state, {
        plan: { $set: action.plan },
        benefitPlanId: { $set: action.plan.id },
        benefitPlanName: { $set: action.plan.name },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

    case 'updateCoverageTier': {
      const next = update(state, {
        coverageTier: { $set: action.coverageTier },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      next.canAddChild = canAddChild(next);
      next.canAddSpouse = canAddSpouse(next);
      return next;
    }

    case 'updateInvoiceGroup': {
      return update(state, {
        invoiceGroup: { $set: action.invoiceGroup },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });
    }

    case 'updatePrimary': {
      const next = update(state, {
        primary: { [action.field]: { $set: action.value } },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      if (action.field === 'ssn') {
        next.ssnDB.deregister(state.primary.ssn, state.primary.relationshipCode);
        next.ssnDB.register(action.value, state.primary.relationshipCode);
      }

      return next;
    }

    case 'updatePrimaryBenefitStartDate': {
      const value = action.value ?? undefined;
      let newPrimary = {};
      if (value && state.primary.benefitEndDate === formatISODate(subDays(parseISO(value), 1))) {
        newPrimary = { isCancelTerminated: { $set: true }, benefitEndDate: { $set: undefined } };
      }

      const next = update(state, {
        primary: { benefitStartDate: { $set: value }, ...newPrimary },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      return next;
    }

    case 'updatePrimaryBenefitEndDate': {
      let value = action.value ?? undefined;

      let isCancelTerminated = { $set: false };
      if (value && state.primary.benefitStartDate === formatISODate(addDays(parseISO(value), 1))) {
        value = undefined;
        isCancelTerminated = { $set: true };
      }

      const isPastTerminated = { $set: false };

      const next = update(state, {
        primary: { benefitEndDate: { $set: value }, isCancelTerminated, isPastTerminated },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      return next;
    }

    case 'updateDependent': {
      const next = update(state, {
        dependents: { [action.index]: { [action.field]: { $set: action.value } } },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      if (action.field === 'ssn') {
        deregisterDependent(next, state.dependents[action.index]);
        next.ssnDB.register(action.value, state.primary.relationshipCode);
      }

      next.canAddSpouse = canAddSpouse(next);
      return next;
    }

    case 'updateDependentBenefitStartDate': {
      const value = action.value ?? undefined;
      let newDependent = {};
      if (value && state.dependents[action.index].benefitEndDate === formatISODate(subDays(parseISO(value), 1))) {
        newDependent = { isCancelTerminated: { $set: true }, benefitEndDate: { $set: undefined } };
      }

      const next = update(state, {
        dependents: { [action.index]: { benefitStartDate: { $set: value }, ...newDependent } },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      next.canAddSpouse = canAddSpouse(next);
      return next;
    }

    case 'updateDependentBenefitEndDate': {
      let value = action.value ?? undefined;

      let isCancelTerminated = { $set: false };
      if (value && state.dependents[action.index].benefitStartDate === formatISODate(addDays(parseISO(value), 1))) {
        value = undefined;
        isCancelTerminated = { $set: true };
      }

      const isPastTerminated = { $set: false };

      const next = update(state, {
        dependents: { [action.index]: { benefitEndDate: { $set: value }, isCancelTerminated, isPastTerminated } },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      next.canAddSpouse = canAddSpouse(next);
      return next;
    }

    case 'updateDependentAddressIsSameAsPrimary':
      return update(state, {
        dependents: { [action.index]: { isAddressSameAsPrimary: { $set: action.value } } },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

    case 'deleteDependent': {
      const next = update(state, {
        dependents: { $splice: [[action.index, 1]] },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
        $unset: ['focusDependentIndex'], // make sure we can focus an item to the end of the list again
      });

      deregisterDependent(next, state.dependents[action.index]);

      next.canAddSpouse = canAddSpouse(next);
      return next;
    }

    case 'terminateDependent': {
      const next = update(state, {
        dependents: { [action.index]: { benefitEndDate: { $set: today() } } },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      next.canAddSpouse = canAddSpouse(next);
      return next;
    }

    case 'cancelTerminateDependent': {
      const benefitEndDate = { $set: undefined };
      const isCancelTerminated = { $set: true };

      const next = update(state, {
        dependents: { [action.index]: { benefitEndDate, isCancelTerminated } },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
      });

      next.canAddSpouse = canAddSpouse(next);
      return next;
    }

    case 'addSpouse':
      if (!state.canAddSpouse) return state;

      return update(state, {
        dependents: {
          $unshift: [
            {
              isNew: true,
              id: uuidV4(),
              relationshipCode: Relationship.SPOUSE,
              benefitStartDate: today(),
              isAddressSameAsPrimary: true,
            },
          ],
        },
        canAddSpouse: { $set: false },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
        focusDependentIndex: { $set: 0 },
      });

    case 'addChild':
      if (!state.canAddChild) return state;

      return update(state, {
        dependents: {
          $push: [
            {
              isNew: true,
              id: uuidV4(),
              relationshipCode: Relationship.CHILD,
              benefitStartDate: today(),
              isAddressSameAsPrimary: true,
            },
          ],
        },
        isDirty: { $set: true },
        canRevert: { $set: !!state.initState },
        focusDependentIndex: { $set: state.dependents.length },
      });
  }
}

function deregisterDependent(state: State, member: EditMember | undefined) {
  if (member) {
    state.ssnDB.deregister(member.ssn, member.relationshipCode);
  }
}

interface ReducerInitProps {
  ssnDB: SsnDatabase;
  memberGroup?: MemberGroup;
  client: Client;
}

function initReducer({ ssnDB, memberGroup, client }: ReducerInitProps): State {
  ssnDB = ssnDB.clone();

  if (memberGroup) {
    const initState: State = {
      ...memberGroup,
      clientId: client.id,
      ssnDB,
      isNew: false,
      benefitPlanName: memberGroup.benefitPlanName,
      plan: client.plans.find(p => p.id === memberGroup.benefitPlanId),
      invoiceGroup: memberGroup.invoiceGroup,
      canAddSpouse: canAddSpouse(memberGroup),
      canAddChild: canAddChild(memberGroup),
    };

    initState.initState = initState;

    return initState;
  } else {
    return {
      ssnDB,
      isNew: true,
      id: uuidV4(), // This ID is temporary. It will be the empolyee external ID
      clientId: client.id,
      coverageTier: CoverageTier.EMPLOYEE,
      organizationId: client.organizationId,
      benefitPlanName: client.plans.length === 1 ? client.plans[0].name : undefined,
      plan: client.plans.length === 1 ? client.plans[0] : undefined,
      primary: {
        isNew: true,
        id: uuidV4(),
        relationshipCode: Relationship.SELF,
        benefitStartDate: today(),
      },
      dependents: [],
      canAddChild: false,
      canAddSpouse: false,
    };
  }
}

export function useEditMemberGroupController(props: ReducerInitProps) {
  return React.useReducer(editReducer, props, initReducer);
}
